import {
  DependencyList,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
export function useAsyncData<T>(
  loader: () => Promise<T>,
  deps: DependencyList = [],
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setData(await loader());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, deps);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data, loading, error, reload, setData };
}
function extractError(error: unknown): string {
  if (typeof error === "string") return error;

  if (error && typeof error === "object") {
    const err = error as any;

    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      "Something went wrong"
    );
  }

  return "Something went wrong";
}

export function useMutation<TPayload = void, TResult = unknown>(
  mutationFn: (payload: TPayload) => Promise<TResult>
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function mutate(payload?: TPayload): Promise<TResult> {
    try {
      setLoading(true);
      setError(null);

      return await mutationFn(payload as TPayload);
    } catch (error) {
      const message = extractError(error);
      setError(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    mutate,
    loading,
    error,
  };
}
export function useDebouncedValue<T>(value: T, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
export function usePageState(defaultSize = 10) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(defaultSize);
  const state = useMemo(() => ({ page, size }), [page, size]);
  return {
    page,
    size,
    setPage,
    setSize: (next: number) => {
      setPage(0);
      setSize(next);
    },
    state,
  };
}
